import React from 'react';
import { Download, Plus } from 'lucide-react';
import { InlineLoadingState } from '../../LoadingSpinner';
import { EmployeePayrollPeriodPicker } from '../payroll/EmployeePayrollPeriodPicker';
import {
  EmployeeOverviewMultiSelect,
  type EmployeeOverviewFilterOption,
} from '../overview/EmployeeOverviewMultiSelect';
import { PayrollAdjustmentBookingsTable } from './PayrollAdjustmentBookingsTable';
import { PayrollAdjustmentBookingsToolbar } from './PayrollAdjustmentBookingsToolbar';
import { PayrollAdjustmentRulesTable } from './PayrollAdjustmentRulesTable';
import { PayrollAdjustmentSummaryCards } from './PayrollAdjustmentSummaryCards';
import {
  computePayrollAdjustmentSummary,
  filterPayrollAdjustmentRows,
  PAYROLL_ADJUSTMENT_FILTER_DROPDOWN_CLASS,
  sortPayrollAdjustmentRows,
  togglePayrollAdjustmentSort,
  type PayrollAdjustmentSortState,
  type PayrollAdjustmentTableSortColumn,
} from './payrollAdjustmentListShared';
import { usePayrollAdjustmentDrawer } from './usePayrollAdjustmentDrawer';
import {
  buildPayrollAdjustmentMonthRows,
  type PayrollAdjustmentCategory,
  type PayrollAdjustmentEntryRow,
  type PayrollAdjustmentMonthRow,
  type PayrollAdjustmentRuleRow,
  yearMonthFromBounds,
  yearMonthsInRange,
} from '../../../utils/payrollAdjustmentCategories';
import {
  buildPayrollAdjustmentMonthCsv,
  downloadCsvDocument,
  payrollAdjustmentRangeExportFilename,
} from '../../../utils/payrollAdjustmentCsvExport';

type EmployeeBeAbzuegeTabProps = {
  from: string;
  to: string;
  onShift: (direction: -1 | 1) => void;
  onApplyCustomPeriod: (range: { from: string; to: string }) => void;
};

type EmployeeOption = {
  id: string;
  name: string;
  end_date: string | null;
  color_idx: number;
  compensation_type: 'hourly' | 'salary';
};

const MONTH_TABLE_COLUMNS: Array<{
  label: string;
  sortKey: PayrollAdjustmentTableSortColumn;
  align?: 'left' | 'right';
}> = [
  { label: 'Mitarbeiter', sortKey: 'employee' },
  { label: 'Datum', sortKey: 'date' },
  { label: 'Kategorie', sortKey: 'category' },
  { label: 'Lohnart', sortKey: 'lohnart' },
  { label: 'Notiz', sortKey: 'note' },
  { label: 'Betrag', sortKey: 'amount', align: 'right' },
];

export function EmployeeBeAbzuegeTab({ from, to, onShift, onApplyCustomPeriod }: EmployeeBeAbzuegeTabProps) {
  const [viewTab, setViewTab] = React.useState<'month' | 'recurring'>('month');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<PayrollAdjustmentCategory[]>([]);
  const [entries, setEntries] = React.useState<PayrollAdjustmentEntryRow[]>([]);
  const [rules, setRules] = React.useState<PayrollAdjustmentRuleRow[]>([]);
  const [employees, setEmployees] = React.useState<EmployeeOption[]>([]);
  const [search, setSearch] = React.useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<string[]>([]);
  const [typeFilter, setTypeFilter] = React.useState('__all');
  const [sort, setSort] = React.useState<PayrollAdjustmentSortState>({ key: 'date', dir: 'asc' });

  const categoryByKey = React.useMemo(
    () => new Map(categories.map((c) => [c.key, c])),
    [categories],
  );

  const employeeNameById = React.useMemo(
    () => new Map(employees.map((e) => [e.id, e.name])),
    [employees],
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entriesQuery = new URLSearchParams({
        from,
        to,
        yearMonth: yearMonthFromBounds(from),
      });
      const entriesUrl = `/api/employees/payroll-adjustments/entries?${entriesQuery.toString()}`;
      const [catRes, entRes, ruleRes, empRes] = await Promise.all([
        fetch('/api/employees/payroll-adjustments/categories', { credentials: 'include' }),
        fetch(entriesUrl, { credentials: 'include' }),
        fetch('/api/employees/payroll-adjustments/rules', { credentials: 'include' }),
        fetch('/api/employees', { credentials: 'include' }),
      ]);
      const catJson = (await catRes.json()) as { items?: PayrollAdjustmentCategory[]; error?: string };
      const entJson = (await entRes.json()) as { items?: PayrollAdjustmentEntryRow[]; error?: string };
      const ruleJson = (await ruleRes.json()) as { items?: PayrollAdjustmentRuleRow[]; error?: string };
      const empJson = (await empRes.json()) as {
        items?: Array<{
          id: string;
          first_name?: string;
          last_name?: string;
          end_date?: string | null;
          color_idx?: number;
          compensation_type?: string | null;
        }>;
        error?: string;
      };
      if (!catRes.ok) throw new Error(catJson.error || 'Kategorien konnten nicht geladen werden.');
      if (!entRes.ok) throw new Error(entJson.error || 'Buchungen konnten nicht geladen werden.');
      if (!ruleRes.ok) throw new Error(ruleJson.error || 'Regelmäßige Buchungen konnten nicht geladen werden.');
      if (!empRes.ok) throw new Error(empJson.error || 'Mitarbeiter konnten nicht geladen werden.');
      setCategories(Array.isArray(catJson.items) ? catJson.items : []);
      setEntries(Array.isArray(entJson.items) ? entJson.items : []);
      setRules(Array.isArray(ruleJson.items) ? ruleJson.items : []);
      const empItems = Array.isArray(empJson.items) ? empJson.items : [];
      setEmployees(
        empItems.map((e) => ({
          id: e.id,
          name: [e.first_name, e.last_name].filter(Boolean).join(' ').trim() || 'Unbenannter Mitarbeiter',
          end_date: e.end_date ?? null,
          color_idx: Number.isFinite(Number(e.color_idx)) ? Number(e.color_idx) : 0,
          compensation_type: e.compensation_type === 'salary' ? 'salary' : 'hourly',
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const periodRows = React.useMemo(() => {
    const employeeEndDateById = new Map(employees.map((e) => [e.id, e.end_date] as const));
    const seen = new Set<string>();
    const out: PayrollAdjustmentMonthRow[] = [];

    for (const yearMonth of yearMonthsInRange(from, to)) {
      const monthRows = buildPayrollAdjustmentMonthRows(
        entries,
        rules,
        categories,
        yearMonth,
        employeeNameById,
        employeeEndDateById,
      );
      for (const row of monthRows) {
        if (row.booking_date < from || row.booking_date > to) continue;
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        out.push(row);
      }
    }

    return out;
  }, [entries, rules, categories, from, to, employeeNameById, employees]);

  const filterEmployees: EmployeeOverviewFilterOption[] = React.useMemo(
    () =>
      employees.map((e) => ({
        id: e.id,
        name: e.name,
        color_idx: e.color_idx,
        compensation_type: e.compensation_type,
      })),
    [employees],
  );

  const sortedPeriodRows = React.useMemo(() => {
    const filtered = filterPayrollAdjustmentRows(periodRows, {
      search,
      typeFilter,
      categoryByKey,
      employeeIds: selectedEmployeeIds,
    });
    return sortPayrollAdjustmentRows(filtered, sort, categoryByKey, 'asc');
  }, [periodRows, search, selectedEmployeeIds, typeFilter, categoryByKey, sort]);

  const summary = React.useMemo(() => computePayrollAdjustmentSummary(periodRows), [periodRows]);

  const {
    openAdd,
    openEditEntry,
    openEditRule,
    deleteEntry,
    deleteRule,
    drawer,
  } = usePayrollAdjustmentDrawer({
    categories,
    entries,
    rules,
    categoryByKey,
    employees,
    defaultEmployeeId: employees[0]?.id ?? '',
    defaultFromYmd: from,
    onReload: load,
    onAfterSaveRecurring: () => setViewTab('recurring'),
  });

  const tabBtn = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
      active ? 'bg-app-surface text-app-text' : 'text-app-text-muted hover:text-app-text'
    }`;

  const exportCurrentView = () => {
    const csv = buildPayrollAdjustmentMonthCsv(sortedPeriodRows, categories, from.slice(0, 7));
    downloadCsvDocument(csv, payrollAdjustmentRangeExportFilename(from, to));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-app-border bg-app-surface px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <EmployeePayrollPeriodPicker
            from={from}
            to={to}
            onShift={onShift}
            onApplyCustomPeriod={onApplyCustomPeriod}
          />

          <div className="flex rounded-lg bg-app-surface-muted p-1">
            <button type="button" className={tabBtn(viewTab === 'month')} onClick={() => setViewTab('month')}>
              Buchungen im Zeitraum
            </button>
            <button
              type="button"
              className={tabBtn(viewTab === 'recurring')}
              onClick={() => setViewTab('recurring')}
            >
              Regelmäßige Buchungen
            </button>
          </div>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => openAdd(viewTab === 'recurring')}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
          >
            <Plus className="h-4 w-4" />
            Neue Buchung
          </button>
        </div>
      </div>

      <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {loading && categories.length === 0 ? (
          <InlineLoadingState label="Lade Be- und Abzüge…" className="py-16" />
        ) : viewTab === 'month' ? (
          <div className="space-y-4">
            <PayrollAdjustmentSummaryCards {...summary} />

            <PayrollAdjustmentBookingsToolbar
              search={search}
              onSearchChange={setSearch}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              showAddButton={false}
              trailing={
                <EmployeeOverviewMultiSelect
                  employees={filterEmployees}
                  selected={selectedEmployeeIds}
                  onChange={setSelectedEmployeeIds}
                  icon={null}
                  triggerClassName={PAYROLL_ADJUSTMENT_FILTER_DROPDOWN_CLASS}
                  panelAlign="end"
                />
              }
              endTrailing={
                <button
                  type="button"
                  onClick={exportCurrentView}
                  disabled={sortedPeriodRows.length === 0}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3 py-0 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:cursor-not-allowed disabled:opacity-50"
                  title="Aktuelle Ansicht als CSV exportieren"
                >
                  <Download className="h-4 w-4 shrink-0 text-app-text-subtle" />
                  Exportieren
                </button>
              }
            />

            <PayrollAdjustmentBookingsTable
              rows={sortedPeriodRows}
              columns={MONTH_TABLE_COLUMNS}
              sort={sort}
              onSort={(key) => setSort((prev) => togglePayrollAdjustmentSort(prev, key))}
              categoryByKey={categoryByKey}
              emptyMessage="Keine Buchungen im gewählten Zeitraum. Lege oben eine neue Buchung an."
              showEmployeeColumn
              onEditEntry={openEditEntry}
              onEditRule={openEditRule}
              onDeleteEntry={(id) => void deleteEntry(id)}
              onDeleteRule={(id) => void deleteRule(id)}
            />
          </div>
        ) : (
          <PayrollAdjustmentRulesTable
            rules={rules}
            categoryByKey={categoryByKey}
            onEditRule={openEditRule}
            onDeleteRule={(id) => void deleteRule(id)}
          />
        )}
      </div>

      {drawer}
    </div>
  );
}
